<template>
    <app-layout title="Settings">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Settings - Purchasers</template>

                <template #actions>
                    <jet-button type="button" class="h-7 leading-none" @click="openAddPurchaser" >
                        Add Purchaser
                    </jet-button>
                </template>
            </page-header>

            <settings-navigation />

            <div class="flex px-8">
                <div class="flex gap-x-2 w-3/4">
                    <div class="flex flex-col flex-1">
                        <jet-input type="text" class="h-7" name="search" v-model="mainForm.search" @input="handleSubmit" placeholder="Search..."/>
                    </div>
                    <div class="self-end">
                        <jet-tertiary-button type="button" class="py-1 h-7" @click="handleReset" >
                            Reset
                        </jet-tertiary-button>
                    </div>
                </div>
            </div>

            <div class="px-8 mt-2">
                <div class="rounded-t overflow-hidden border border-b-0 w-3/4">
                    <table class="table w-full">
                        <thead>
                            <tr>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="name">Name</sortable-header>
                                <th class="text-left">Email</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="purchaser in purchasers.data" :key="purchaser.id" class="cursor-pointer" @click="editPurchaser(purchaser)">
                                <td class="text-left">
                                    {{ purchaser.name }}
                                </td>
                                <td class="text-left">
                                    {{ purchaser.email }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex justify-between items-center mt-4 w-3/4">
                    <pagination class="mt-0 mb-0" :links="purchasers.links" />

                    <div class="w-40">
                        <multiselect
                            v-model.number="mainForm.paginate"
                            :options="paginateOptions"
                            :canDeselect="false"
                            :canClear="false"
                            :classes="{ singleLabel: 'w-full px-2' }"
                            class="h-8 multi-sm multi-custom text-base"
                            openDirection="top"
                            @change="handleSubmit"
                        />
                    </div>
                </div>
            </div>

            <!-- Add / Edit Purchaser -->
            <jet-dialog-modal :show="showPurchaserModal" @close="closePurchaserModal">
                <template #title>
                     <h3 class="text-2xl">{{ showPurchaserModal == 'add' ? 'Add' : 'Edit' }} Purchaser</h3>
                </template>

                <template #content>
                    <form @submit.prevent="submitForm" class="my-6">
                        <div>
                            <jet-label value="Name:" class="mb-0" style="min-width: 60px;" />
                            <jet-input v-model="form.name" type="text" class="w-full" required />
                            <jet-input-error class="mt-1" :message="form.errors.name || clientErrors.name" />
                        </div>

                        <div class="mt-4">
                            <jet-label value="Email:" class="mb-0" style="min-width: 60px;" />
                            <jet-input v-model="form.email" type="email" class="w-full" required autocomplete="email" />
                            <jet-input-error class="mt-1" :message="form.errors.email || clientErrors.email" />
                        </div>

                        <input type="submit" class="hidden" ref="submitFormButton" />
                    </form>
                </template>

                <template #footer>
                    <jet-danger-button v-if="showPurchaserModal === 'edit'" class="mr-2" @click="showDeleteModal = true">
                        Delete
                    </jet-danger-button>

                    <jet-button class="mr-2" @click="$refs.submitFormButton.click()" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button @click="closePurchaserModal">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>

            <!-- Delete Purchaser -->
            <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal = false">
                <template #title>
                    Delete Purchaser
                </template>

                <template #content>
                    Are you sure you would like to delete purchaser?
                </template>

                <template #footer>
                    <jet-danger-button @click="handleDeletePurchaser">
                        Yes
                    </jet-danger-button>

                    <jet-tertiary-button class="ml-2" @click="showDeleteModal = false">
                        No
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>
        </form>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';
import Pagination from '@/Jetstream/Pagination'
import SortableHeader from '@/Shared/SortableHeader';
import Toggle from '@vueform/toggle';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

export default {
    setup(props) {
        const mainForm = useForm({
            paginate: props.paginate,
            search: props.search,
            sortColumn: props.sortColumn,
            sortDirection: props.sortDirection,
        });

        const form = useForm({
            id: null,
            name: '',
            email: '',
        });

        return { mainForm, form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetLabel,
        JetInput,
        JetInputError,
        JetDialogModal,
        JetConfirmationModal,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        SettingsNavigation,
        Multiselect,
        Pagination,
        SortableHeader,
        Toggle,
    },

    props: [
        'purchasers',
        'search',
        'sortColumn',
        'sortDirection',
        'paginate',
    ],

    data() {
        return {
            showPurchaserModal: false,
            showDeleteModal: false,
            clientErrors: {
                name: null,
                email: null,
            },

            paginateOptions: [
                { value: 10, label: 'Show 10' },
                { value: 20, label: 'Show 20' },
                { value: 50, label: 'Show 50' },
                { value: 100, label: 'Show 100' },
            ],
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.mainForm.get(route('settings.purchasers.index'), { preserveState: true });
        }, 250),

        openAddPurchaser() {
            this.form.reset();
            this.form.clearErrors();
            this.clientErrors = { name: null, email: null };
            this.showPurchaserModal = 'add';
        },

        editPurchaser(data) {
            this.form.id = data.id;
            this.form.name = data.name;
            this.form.email = data.email ?? '';
            this.clientErrors = { name: null, email: null };
            this.showPurchaserModal = 'edit';
        },

        isValidEmailFormat(value) {
            const v = (value || '').trim();
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
        },

        handleReset() {
            this.mainForm.search = '';
            this.mainForm.sortColumn = 'id';
            this.mainForm.sortDirection = 'asc';
            this.handleSubmit();
        },

        handleSort(sort) {
            this.mainForm.sortColumn = sort.column;
            this.mainForm.sortDirection = sort.direction;
            this.handleSubmit();
        },

        closePurchaserModal() {
            this.form.reset();
            this.form.clearErrors();
            this.clientErrors = { name: null, email: null };
            this.showPurchaserModal = false;
            this.showDeleteModal = false;
        },

        submitForm() {
            const name = (this.form.name || '').trim();
            const email = (this.form.email || '').trim();
            this.clientErrors = { name: null, email: null };
            if (!name) {
                this.clientErrors.name = 'The name field is required.';
                return;
            }
            if (!email) {
                this.clientErrors.email = 'The email field is required.';
                return;
            }
            if (!this.isValidEmailFormat(email)) {
                this.clientErrors.email = 'Please enter a valid email address.';
                return;
            }

            if (this.showPurchaserModal === 'add') {
                this.form.post(this.route('settings.purchasers.store'), {
                    onSuccess: () => this.closePurchaserModal()
                });

                return true;
            }

            this.form.put(this.route('settings.purchasers.update', this.form.id), {
                onSuccess: () => this.closePurchaserModal()
            });
        },

        handleDeletePurchaser() {
            this.form.delete(this.route('settings.purchasers.destroy', this.form.id), {
                onSuccess: () => this.closePurchaserModal()
            });
        },
    },
}
</script>

<style scoped>
</style>
